<template>
  <div class="extension-column">
    <div class="overview-sec">
      次の延長
    </div>

    <div v-if="hasExtensionCandidateCourses">
      <extension-preset-card
        v-for="(extensionCandidate, extensionCandidateIdx) in extensionCandidates"
        :key="`extensionCandidates[${extensionCandidateIdx}]`"
        :candidate="extensionCandidate"
        :estimated-total="getTotal(extensionCandidateIdx)"
        :is-calculating="isCalculating"
        :recommended="extensionCandidateIdx === recommendedIndex"
        :submitting="submittingIdx === extensionCandidateIdx"
        @submit="submitExtension(extensionCandidateIdx)"
      />

      <print-btn
        subject="概算伝票"
        :block="true"
        :print="printEstimatedSlip"
        class="mt-4"
      >
        <template #append>
          <Preview
            :slip-svg="slipSvg"
            class="mt-1"
          />
        </template>
      </print-btn>
    </div>

    <div v-else>
      <v-skeleton-loader
        v-if="isInitializing"
        height="160"
        type="card"
      />

      <p
        v-else
        class="ma-0 ml-2 pa-0 text-sm text--secondary"
      >
        延長候補はありません
      </p>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  inject,
  watch,
  onUnmounted,
  getCurrentInstance,
  onMounted,
} from '@vue/composition-api'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import useExtensionCandidates, { pickRecommendedProposalIndex } from '@/views/composable/useExtensionCandidates'
import useExtensionSlipPrint from '@/views/composable/useExtensionSlipPrint'
import useCurrentData from '@/views/composable/useCurrentData'
import useKitchenPrint from '@/views/composable/useKitchenPrint'
import ExtensionPresetCard from './ExtensionPresetCard.vue'
import PrintBtn from '@/views/components/util/PrintBtn.vue'
import Preview from '@/views/components/printer/Preview.vue'

export default {
  components: {
    ExtensionPresetCard,
    PrintBtn,
    Preview,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    totalDepositAmount: {
      type: [Number, String],
      default: 0,
    },
    hallName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const vendingHistories = inject('vendingHistories', ref([]))

    const { currentUser } = useCurrentData()
    const { printKitchenSlips } = useKitchenPrint()

    const submittingIdx = ref(null)

    const {
      extensionProposals,
      extensionCandidates,
      isInitializing,
      estimatedTotals,
      isCalculating,
      hasExtensionCandidateCourses,
      vendingHistoriesToSubmitOfArray,
      fetchExtensionProposals,
      getTotalTax,
      getTotal,
      resetCandidates,
    } = useExtensionCandidates(props)

    const recommendedIndex = computed(() => {
      return pickRecommendedProposalIndex(extensionProposals.value, vendingHistories.value)
    })

    // NOTE: 確認モーダルを挟まず即時にバックエンドへ延長データを作成する
    const submitExtension = async index => {
      if (submittingIdx.value !== null) return

      submittingIdx.value = index

      try {
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: vendingHistoriesToSubmitOfArray.value[index],
        })

        if (res?.status === 200) {
          emit('updated:vending-histories', res.data.vendingHistories.data)
          // NOTE: updated:tableでprops.totalが変化しwatchが発火するが、
          // submittingIdxガードでblockされるので二重フェッチしない
          emit('updated:table', res.data.table.data)

          // キッチンプリンター印刷（既存の注文フローと同等）
          const tableCode = props.customName || `${props.tablePrefix}${props.tableNumber}${props.tableSuffix}`
          printKitchenSlips({
            vendingHistories: res.data.vendingHistories.data,
            staffName: currentUser.value?.name || '',
            hallName: props.hallName,
            tableCode,
            customerCount: props.customerCount,
          })

          vm.$toast.success('延長しました')

          // NOTE: 延長後は候補が変わるため取り直す
          resetCandidates()
          await fetchExtensionProposals()
        } else {
          vm.$toast.error('延長に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長に失敗しました')
      } finally {
        submittingIdx.value = null
      }
    }

    // NOTE: 取り消し・注文・客数変更などで卓の合計が変わったら見積が古くなるため候補ごと取り直す
    // （延長実行中はsubmittingIdxガードでblockされ二重取得しない）
    watch(() => props.total, async () => {
      if (submittingIdx.value !== null) return

      resetCandidates()
      await fetchExtensionProposals()
    })

    // NOTE: YONAREZI 3 概算伝票
    const { slipSvg, printEstimatedSlip } = useExtensionSlipPrint({
      props,
      extensionCandidates,
      estimatedTotals,
      getTotalTax,
    })

    onUnmounted(() => resetCandidates())

    onMounted(async () => {
      await fetchExtensionProposals()
    })

    return {
      extensionCandidates,
      isInitializing,
      isCalculating,
      hasExtensionCandidateCourses,
      recommendedIndex,
      submittingIdx,
      slipSvg,

      getTotal,
      submitExtension,
      printEstimatedSlip,
    }
  },
}
</script>
